import { useRef, type ReactNode } from 'react';
import { Car, ImageOff } from 'lucide-react';
import type { PlateDetection } from '@iot-dashboard/shared';
import { usePlateThumbnail } from '../api/hooks';
import { useInView } from '../lib/useInView';
import { plateLabel } from '../lib/plates';
import { useObjectUrl } from '../lib/useObjectUrl';

interface Props {
  tenantId: string;
  read: PlateDetection;
  onOpen: () => void;
}

/**
 * The small picture beside a plate read. The picture sits behind the API's
 * bearer token, so it is fetched and shown as an object URL, and only once its
 * row nears the screen, so a long list does not ask for every picture at once.
 */
export function PlateThumbnail({ tenantId, read, onOpen }: Props) {
  const ref = useRef<HTMLButtonElement>(null);
  const inView = useInView(ref);
  const hasPicture = Boolean(read.hasThumbnail);
  const picture = usePlateThumbnail(tenantId, read.detectionId, hasPicture && inView);
  const src = useObjectUrl(picture.data);
  const plate = plateLabel(read);

  let content: ReactNode = null;
  let state = '';
  if (src) {
    content = <img className="plate-thumb__image" src={src} alt="" />;
  } else if (!hasPicture) {
    content = <Car size={18} aria-hidden="true" />;
    state = ' plate-thumb--none';
  } else if (picture.isError) {
    content = <ImageOff size={16} aria-hidden="true" />;
    state = ' plate-thumb--none';
  } else {
    state = ' plate-thumb--loading';
  }

  return (
    <button
      ref={ref}
      type="button"
      className={`plate-thumb${state}`}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      aria-label={hasPicture ? `View the picture of ${plate}` : `View ${plate}; it has no picture`}
      title={hasPicture ? 'View the picture' : 'No picture with this read'}
    >
      {content}
    </button>
  );
}
